import React from 'react';
import { useTranslation } from 'react-i18next';

import useImportantNotifications from '../../hooks/useImportantNotifications';
import { Modal } from '../ui';

/**
 * The dialogue that opens by itself while an `IMPORTANT` notification is
 * unread — the stand-in for the old red envelope in the corner, which popped a
 * semantic-ui list of 200px-tall cards over the page.
 *
 * The design has no frame for it, so it is built from the module's own `Modal`
 * and the notification tile's type: the title, the time it arrived, the whole
 * body — not clamped, this is where it gets read — and the picture only when
 * there is one. The old popup stood the site logo in at full height for every
 * notification sent without a picture, which is most of them.
 *
 * Only the unread ones are listed. Once dismissed they sit in the bell with the
 * rest, which is where the envelope used to keep them.
 *
 * `StudentLayout` mounts it on every signed-in page, but not during an exam and
 * not over the profile-completion modal.
 */
const ImportantNotifications = () => {
    const { t, i18n } = useTranslation();
    const { items, dismiss } = useImportantNotifications();

    return (
        <Modal
            isOpen={items.length > 0}
            title={t('v2.student.notifications.important')}
            onClose={dismiss}
        >
            <div className="tw-flex tw-flex-col tw-gap-[16px]">
                {items.map((n, index) => (
                    <Entry
                        key={n.notificationId ?? index}
                        notification={n}
                        language={i18n.language}
                        onOpen={dismiss}
                    />
                ))}
            </div>

            <button
                type="button"
                onClick={dismiss}
                className={
                    'tw-mt-[20px] tw-flex tw-h-[39px] tw-w-full tw-items-center tw-justify-center ' +
                    'tw-rounded-[8.757px] tw-bg-v2-brand tw-text-[16px] tw-leading-[1.6] tw-text-white ' +
                    'tw-transition hover:tw-brightness-105 focus-visible:tw-outline-none ' +
                    'focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand focus-visible:tw-ring-offset-2'
                }
            >
                {t('v2.student.notifications.dismiss')}
            </button>
        </Modal>
    );
};

/**
 * One notification in the dialogue. A link when it carries somewhere to go —
 * `href` is '#' for the hand-written ones, and a card that looks clickable and
 * goes nowhere is worse than one that does not look clickable.
 */
const Entry = ({ notification, language, onOpen }) => {
    const { title, content, imgUrl, creatAt, href } = notification;
    const stamp = creatAt ? new Date(creatAt).toLocaleString(language) : '';
    const isLink = Boolean(href) && href !== '#';
    const Tag = isLink ? 'a' : 'article';

    return (
        <Tag
            {...(isLink ? { href, onClick: onOpen } : {})}
            className={
                'tw-flex tw-flex-col tw-overflow-hidden tw-rounded-[12px] tw-bg-white ' +
                'tw-border tw-border-solid tw-border-v2-line ' +
                'tw-no-underline hover:tw-no-underline focus:tw-no-underline ' +
                (isLink
                    ? 'tw-transition-colors hover:tw-bg-v2-neutral-50 focus-visible:tw-outline-none ' +
                      'focus-visible:tw-ring-2 focus-visible:tw-ring-v2-brand'
                    : '')
            }
        >
            {imgUrl && (
                <img
                    src={imgUrl}
                    alt=""
                    aria-hidden
                    className="tw-max-h-[320px] tw-w-full tw-shrink-0 tw-bg-v2-ground-300 tw-object-contain"
                />
            )}

            <div className="tw-flex tw-flex-col tw-gap-[6px] tw-p-[16px] tw-text-start">
                <p className="tw-m-0 tw-text-[16px] tw-font-bold tw-leading-[1.5] tw-text-v2-neutral-900">
                    {title}
                </p>

                <p className="tw-m-0 tw-text-[12px] tw-leading-[1.5] tw-text-v2-brand">
                    <bdi dir="auto">{stamp}</bdi>
                </p>

                <p className="tw-m-0 tw-whitespace-pre-line tw-text-[14px] tw-leading-[1.7] tw-text-v2-student-meta">
                    {content}
                </p>
            </div>
        </Tag>
    );
};

export default ImportantNotifications;
